import { TFunction } from 'i18next';

// Маппинг type → ключ перевода для модулей loyalty program
const LOYALTY_MODULE_TYPE_MAP: Record<string, string> = {
  foods: 'nutrition',
  tickets: 'applications',
  feedback: 'feedback',
  user: 'loyalty_module_user',
  questionnaire: 'section_questionnaire',
  entertainment: 'services',
};

// Маппинг type → ключ перевода для секций админки и мобильного приложения
const SECTION_TYPE_MAP: Record<string, string> = {
  accommodation: 'section_accommodation',
  food: 'nutrition',
  orders: 'orders',
  employees: 'employees',
  ticket: 'applications',
  feedback: 'feedback',
  announcement: 'announcements',
  questionnaire: 'section_questionnaire',
  events: 'section_events',
  entertainment: 'section_entertainment',
  loyalty_program: 'section_loyalty_program',
  notification: 'section_notification',
  qr_generator: 'section_qr_generator',
  food_analytic: 'section_food_analytic',
  energy_value: 'section_energy_value',
  knowledge_base: 'section_knowledge_base',
  transactions: 'section_transactions',
};

// Тип точки питания
const FOOD_POINT_TYPE_MAP: Record<string, string> = {
  dining: 'canteen',
  cafeteria: 'cafeteria',
};

// Категории/типы блюд (сервис) — маппинг по name (приходит с бэка на русском или английском)
const FOOD_TYPE_NAME_MAP: Record<string, string> = {
  'Завтрак': 'breakfast',
  'Обед': 'lunch',
  'Ужин': 'dinner',
  'Breakfast': 'breakfast',
  'Lunch': 'lunch',
  'Dinner': 'dinner',
};

// Единицы измерения — маппинг по значению из API
const MEASURE_MAP: Record<string, string> = {
  'г': 'g',
  'мл': 'ml',
  'шт': 'pcs',
  'кг': 'kg',
  'л': 'l',
};

// Тип оплаты
const PAYMENT_TYPE_MAP: Record<string, string> = {
  employee_card: 'payment_employee_card',
  bank_card: 'payment_bank_card',
  kso: 'payment_kso',
};

// Роли пользователей
const ROLE_NAME_MAP: Record<string, string> = {
  'super-admin': 'role_super_admin',
  admin: 'role_admin',
  user: 'role_user',
  'asset-admin': 'role_asset_admin',
  'food-admin': 'role_food_admin',
};

// Грейды пользователей (маппинг по английскому имени, т.к. бэк может вернуть English)
const GRADE_NAME_MAP: Record<string, string> = {
  VIP: 'grade_vip',
  Employee: 'grade_employee',
  'Сотрудник': 'grade_employee',
};

// Категории блокировки
const BLOCK_CATEGORY_MAP: Record<string, string> = {
  tickets: 'applications',
  feedbacks: 'feedback',
  orders: 'orders',
};

interface OptionWithType {
  id: number | string;
  name: string;
  type?: string;
  display_name?: string;
  [key: string]: any;
}

/**
 * Добавляет display_name с переводом к массиву опций на основе маппинга type → ключ перевода
 */
const translateOptionsByMap = <T extends OptionWithType>(
  options: T[] | undefined,
  typeMap: Record<string, string>,
  t: TFunction,
  keyField: 'type' | 'name' = 'type'
): T[] => {
  if (!options) return [];
  return options.map(option => {
    const key = option[keyField] as string | undefined;
    const translationKey = key ? typeMap[key] : undefined;
    return {
      ...option,
      display_name: translationKey ? t(translationKey) : option.display_name || option.name,
    };
  });
};

export const translateSectionOptions = <T extends OptionWithType>(
  options: T[] | undefined,
  t: TFunction
) => translateOptionsByMap(options, SECTION_TYPE_MAP, t);

export const translateFoodPointTypes = <T extends OptionWithType>(
  options: T[] | undefined,
  t: TFunction
) => translateOptionsByMap(options, FOOD_POINT_TYPE_MAP, t);

/**
 * Переводит единичный объект food_point_type (для formik.values.pointObject)
 */
export const translateFoodPointTypeItem = <T extends OptionWithType>(
  item: T | null | undefined,
  t: TFunction
): T | null | undefined => {
  if (!item) return item;
  const key = item.type ? FOOD_POINT_TYPE_MAP[item.type] : undefined;
  return { ...item, display_name: key ? t(key) : item.display_name || item.name };
};

export const translatePaymentTypes = <T extends OptionWithType>(
  options: T[] | undefined,
  t: TFunction
) => translateOptionsByMap(options, PAYMENT_TYPE_MAP, t);

/**
 * Переводит единичный объект payment_type (для отображения в PaymentBlock)
 */
export const translatePaymentTypeItem = <T extends OptionWithType>(
  item: T | null | undefined,
  t: TFunction
): T | null | undefined => {
  if (!item) return item;
  const key = item.type ? PAYMENT_TYPE_MAP[item.type] : undefined;
  return { ...item, display_name: key ? t(key) : item.display_name || item.name };
};

/**
 * Переводит массив food_types (категорий блюд) по name
 */
export const translateFoodTypes = <T extends OptionWithType>(
  options: T[] | undefined,
  t: TFunction
) => translateOptionsByMap(options, FOOD_TYPE_NAME_MAP, t, 'name');

/**
 * Переводит единичный объект food_type (категория блюда)
 */
export const translateFoodTypeItem = <T extends OptionWithType>(
  item: T | null | undefined,
  t: TFunction
): T | null | undefined => {
  if (!item) return item;
  const key = FOOD_TYPE_NAME_MAP[item.name as string];
  return { ...item, display_name: key ? t(key) : item.display_name || item.name };
};

/**
 * Переводит единицу измерения из API (русская) в локализованную
 */
export const translateMeasure = (measure: string, t: TFunction): string => {
  const normalized = measure?.trim().toLowerCase();
  const key = Object.entries(MEASURE_MAP).find(
    ([ru]) => ru.toLowerCase() === normalized
  )?.[1];
  return key ? t(key) : measure;
};

export const translateRoles = <T extends OptionWithType>(
  options: T[] | undefined,
  t: TFunction
) => translateOptionsByMap(options, ROLE_NAME_MAP, t, 'name');

export const translateGrades = <T extends OptionWithType>(
  options: T[] | undefined,
  t: TFunction
) => translateOptionsByMap(options, GRADE_NAME_MAP, t, 'name');

export const translateBlockCategories = <T extends OptionWithType>(
  options: T[] | undefined,
  t: TFunction
) => translateOptionsByMap(options, BLOCK_CATEGORY_MAP, t);

export const translateLoyaltyModules = <T extends OptionWithType>(
  options: T[] | undefined,
  t: TFunction
) => translateOptionsByMap(options, LOYALTY_MODULE_TYPE_MAP, t);

/**
 * Переводит единичный объект роли (для formik.values.role)
 */
export const translateRoleItem = <T extends OptionWithType>(
  item: T | null | undefined,
  t: TFunction
): T | null | undefined => {
  if (!item) return item;
  const key = ROLE_NAME_MAP[item.name as string];
  return { ...item, display_name: key ? t(key) : item.display_name || item.name };
};

/**
 * Переводит единичный объект грейда (для formik.values.grade)
 */
export const translateGradeItem = <T extends OptionWithType>(
  item: T | null | undefined,
  t: TFunction
): T | null | undefined => {
  if (!item) return item;
  const key = GRADE_NAME_MAP[item.name as string];
  return { ...item, display_name: key ? t(key) : item.display_name || item.name };
};

/**
 * Переводит название грейда пользователя (строку)
 */
export const translateGradeName = (name: string, t: TFunction): string => {
  const key = GRADE_NAME_MAP[name];
  return key ? t(key) : name;
};

// Маппинг русских названий категорий тикетов/обратной связи → ключ перевода
const CATEGORY_NAME_MAP: Record<string, string> = {
  'Размещение': 'category_accommodation',
  'Питание': 'nutrition',
  'Свободная форма': 'free_form',
  'Услуга': 'category_service',
  'Заявка': 'applications',
  'Accommodation': 'category_accommodation',
  'Food': 'nutrition',
  'Free form': 'free_form',
  'Ticket': 'applications',
  'Service': 'category_service',
};

/**
 * Переводит название категории тикета/обратной связи
 */
export const translateCategoryName = (name: string, t: TFunction): string => {
  const key = CATEGORY_NAME_MAP[name];
  return key ? t(key) : name;
};

// Статусы сервисов (entertainment)
const ENTERTAINMENT_STATUS_MAP: Record<string, string> = {
  active: 'entertainment_status_active',
  default: 'entertainment_status_active',
  inactive: 'entertainment_status_inactive',
};

export const translateEntertainmentStatus = (type: string, t: TFunction): string => {
  const key = ENTERTAINMENT_STATUS_MAP[type];
  return key ? t(key) : type;
};

// Статусы слотов
const SLOT_STATUS_MAP: Record<string, string> = {
  active: 'slot_status_active',
  blocked: 'slot_status_inactive',
  inactive: 'slot_status_inactive',
  full: 'slot_status_full',
  closed: 'slot_status_closed',
};

export const translateSlotStatus = (type: string, t: TFunction): string => {
  const key = SLOT_STATUS_MAP[type];
  return key ? t(key) : type;
};

// Типы анкет (маппинг по русскому name и по type-коду)
const QUESTIONNAIRE_TYPE_MAP: Record<string, string> = {
  'Качество питания': 'questionnaire_type_food_quality',
  'Качество размещения': 'questionnaire_type_accommodation_quality',
  'Свободная форма': 'questionnaire_type_free_form',
  food_quality: 'questionnaire_type_food_quality',
  accommodation_quality: 'questionnaire_type_accommodation_quality',
  free_form: 'questionnaire_type_free_form',
};

export const translateQuestionnaireType = (name: string, type?: string, t?: TFunction): string => {
  if (!t) return name;
  const key = (type && QUESTIONNAIRE_TYPE_MAP[type]) || QUESTIONNAIRE_TYPE_MAP[name];
  return key ? t(key) : name;
};

// Статусы анкет
const QUESTIONNAIRE_STATUS_MAP: Record<string, string> = {
  created: 'questionnaire_status_created',
  active: 'questionnaire_status_active',
  closed: 'questionnaire_status_closed',
  completed: 'questionnaire_status_completed',
  'Создана': 'questionnaire_status_created',
  'Активна': 'questionnaire_status_active',
  'Закрыта': 'questionnaire_status_closed',
  'Завершена': 'questionnaire_status_completed',
};

export const translateQuestionnaireStatus = (name: string, type?: string, t?: TFunction): string => {
  if (!t) return name;
  const key = (type && QUESTIONNAIRE_STATUS_MAP[type]) || QUESTIONNAIRE_STATUS_MAP[name];
  return key ? t(key) : name;
};
